import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Activity, TrendingUp, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface SocieteRanking {
  societe_id: string;
  societe_nom: string;
  nb_candidatures: number;
  score_moyen: number;
  scores_criteres: { [key: string]: { score: number; count: number } };
}

interface RadarComparisonProps {
  rankings: SocieteRanking[];
  selectedCompanies: string[];
  selectedCriteria: string[];
}

interface SelectedDataPoint {
  company: string;
  criteriaKey: string;
  criteriaLabel: string;
  value: number;
  ranking: SocieteRanking;
}

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  '#8b5cf6',
  '#06b6d4',
  '#84cc16',
  '#f59e0b',
  '#ef4444'
];

export const RadarComparison: React.FC<RadarComparisonProps> = ({
  rankings,
  selectedCompanies,
  selectedCriteria
}) => {
  const [selectedDataPoint, setSelectedDataPoint] = useState<SelectedDataPoint | null>(null);

  const getCriteriaLabel = (criteriaKey: string): string => {
    if (criteriaKey === 'overall_score') return 'Score Global';
    if (criteriaKey.startsWith('comp_')) return criteriaKey.replace('comp_', '');
    if (criteriaKey.startsWith('cert_')) return criteriaKey.replace('cert_', '');
    return criteriaKey;
  };

  const getScoreLevel = (score: number): { label: string; color: string } => {
    if (score >= 80) return { label: 'Excellent', color: 'text-green-600' };
    if (score >= 60) return { label: 'Bon', color: 'text-blue-600' };
    if (score >= 40) return { label: 'Moyen', color: 'text-yellow-600' };
    return { label: 'Faible', color: 'text-red-600' };
  };

  const getRankForCriteria = (ranking: SocieteRanking, criteriaKey: string): number => {
    const selectedRankings = rankings.filter(r => selectedCompanies.includes(r.societe_id));
    const scores = selectedRankings.map(r => {
      if (criteriaKey === 'overall_score') return { id: r.societe_id, score: r.score_moyen };
      if (criteriaKey.startsWith('criteria_')) {
        const criteriaName = criteriaKey.replace('criteria_', '');
        return { id: r.societe_id, score: r.scores_criteres[criteriaName]?.score || 0 };
      }
      return { id: r.societe_id, score: 0 };
    });
    scores.sort((a, b) => b.score - a.score);
    return scores.findIndex(s => s.id === ranking.societe_id) + 1;
  };

  if (selectedCompanies.length === 0 || selectedCriteria.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Graphique Radar Comparatif
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Sélectionnez des sociétés et des critères pour afficher la comparaison
          </div>
        </CardContent>
      </Card>
    );
  }

  const selectedRankings = rankings.filter(r => selectedCompanies.includes(r.societe_id));

  // Prepare data for radar chart
  const radarData = selectedCriteria.map(criteriaKey => {
    const dataPoint: any = { criteria: getCriteriaLabel(criteriaKey), criteriaKey };
    
    selectedRankings.forEach((ranking) => {
      let value = 0;
      
      if (criteriaKey === 'overall_score') {
        value = ranking.score_moyen;
      } else if (criteriaKey.startsWith('criteria_')) {
        const criteriaName = criteriaKey.replace('criteria_', '');
        value = ranking.scores_criteres[criteriaName]?.score || 0;
      }
      
      dataPoint[ranking.societe_nom] = Math.round(value);
    });
    
    return dataPoint;
  });

  const handleRadarClick = (data: any) => {
    if (data && data.activePayload && data.activePayload.length > 0) {
      const payload = data.activePayload[0];
      const criteriaKey = radarData.find(d => d.criteria === data.activeLabel)?.criteriaKey;
      const ranking = selectedRankings.find(r => r.societe_nom === payload.dataKey);
      
      if (criteriaKey && ranking) {
        setSelectedDataPoint({
          company: payload.dataKey,
          criteriaKey,
          criteriaLabel: data.activeLabel,
          value: payload.value,
          ranking
        });
      }
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium text-foreground mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-foreground">
                {entry.dataKey}: {entry.value}%
              </span>
            </div>
          ))}
          <p className="text-xs text-muted-foreground mt-2">Cliquez pour plus de détails</p>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Graphique Radar Comparatif
          </CardTitle>
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedRankings.map((ranking, index) => (
              <Badge 
                key={ranking.societe_id} 
                variant="outline"
                className="flex items-center gap-1"
                style={{ borderColor: COLORS[index % COLORS.length] }}
              >
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                {ranking.societe_nom}
              </Badge>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[600px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart 
                data={radarData} 
                margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
                onClick={handleRadarClick}
              >
                <PolarGrid className="stroke-border" />
                <PolarAngleAxis 
                  dataKey="criteria" 
                  className="text-xs fill-muted-foreground"
                  tick={{ fontSize: 12 }}
                />
                <PolarRadiusAxis 
                  angle={90} 
                  domain={[0, 100]} 
                  className="text-xs fill-muted-foreground"
                  tick={{ fontSize: 10 }}
                />
                {selectedRankings.map((ranking, index) => (
                  <Radar
                    key={ranking.societe_id}
                    name={ranking.societe_nom}
                    dataKey={ranking.societe_nom}
                    stroke={COLORS[index % COLORS.length]}
                    fill={COLORS[index % COLORS.length]}
                    fillOpacity={0.1}
                    strokeWidth={2}
                  />
                ))}
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="circle"
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!selectedDataPoint} onOpenChange={() => setSelectedDataPoint(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Détails du score
            </DialogTitle>
          </DialogHeader>
          {selectedDataPoint && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Société</p>
                  <p className="font-medium">{selectedDataPoint.company}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Critère</p>
                  <p className="font-medium">{selectedDataPoint.criteriaLabel}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Score</p>
                  <p className="text-2xl font-bold">{selectedDataPoint.value}%</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Note /5</p>
                  <p className="text-2xl font-bold">{(selectedDataPoint.value / 20).toFixed(1)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Niveau</p>
                  <p className={`text-lg font-semibold ${getScoreLevel(selectedDataPoint.value).color}`}>
                    {getScoreLevel(selectedDataPoint.value).label}
                  </p>
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Classement pour ce critère</p>
                <p className="font-medium">
                  {getRankForCriteria(selectedDataPoint.ranking, selectedDataPoint.criteriaKey)} / {selectedRankings.length}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  Candidatures évaluées
                </p>
                <p className="font-medium">{selectedDataPoint.ranking.nb_candidatures}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
